// InteracSuccess.jsx
import React, { useContext, useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { centsToDollars } from 'shared-components/src/utils/currency';
import HtmlRenderer from 'shared-components/src/components/HtmlRenderer';
import InteracImg from '../../images/Interac-transfer.png';
import {
  Container,
  Center,
  SuccessIconRing,
  Title,
  Subtitle,
  DCard,
  DCardBody,
  CardHeaderRow,
  Row,
  LabelText,
  ValueText,
  Breakable,
  ButtonRow,
} from './styles';
import { ProgressBar, ProgressFill } from '../../style';
import { OutlineButton, PrintableDiv } from '../../TransactionSuccess/styles';
import PrintOnlyStyles, {
  printSamePage,
} from '../../TransactionSuccess/PrintOnlyStyles';
import {
  downloadElementAsPDF,
  shareElementAsImage,
} from '../../shareDownloadHelpers';
import { DisbursementsContext } from '../../../../contexts/DisbursementsContext';
import { formatIntlCurrency } from '../../../../utils/helpers';
import { CheckGreenIcon, DownloadIcon, ShareIcon } from '../../../../components/Icons';

function InteracSuccess() {
  const navigate = useNavigate();
  const printableRef = useRef(null);
  const intl = useIntl();
  const { transferSuccessData = {} } = useContext(DisbursementsContext);
  const {
    amount = 0,
    processor_reference: processorReference = '',
    id: transactionId = '',
    transfer = {},
    external_account_info: externalAccountInfo,
    entered_email: enteredEmail = '',
  } = transferSuccessData || {};
  // Where the money went: the address the e-Transfer processor has on file, else the one entered.
  const recipientEmail = (externalAccountInfo && externalAccountInfo.email) || enteredEmail;
  const { amount: transferAmount = 0, fee = 0, transferred_at: transferredAt } = transfer || {};
  const originalAmount = formatIntlCurrency(
    centsToDollars(amount || 0),
    intl.locale
  );
  const amountTranferred = formatIntlCurrency(
    centsToDollars(transferAmount || 0),
    intl.locale
  );
  const transferFee = formatIntlCurrency(centsToDollars(fee || 0), intl.locale);
  const transferDate = new Date(transferredAt).toLocaleDateString('en-CA', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

  const handleShare = async () => {
    const el = printableRef.current;
    if (!el) return;

    const ok = await shareElementAsImage(el, {
      title: intl.messages['success.title'],
      text: intl.messages['success.subtitle.completed'],
    });

    if (!ok) {
      // Fallback: same-page print if Web Share isn’t supported
      printSamePage('#printable');
    }
  };

  const handleDownload = async () => {
    const el = printableRef.current;
    if (!el) return;
    await downloadElementAsPDF(el, {
      fileName: `receipt-${transactionId}.pdf`,
    });
  };

  useEffect(() => {
    if (!transferSuccessData) {
      navigate('/disbursements/redeem');
    }
  }, [transferSuccessData, navigate]);
  const urlParams = new URLSearchParams(
    typeof window !== 'undefined' ? window.location.search : ''
  );
  const isAutoDeposit = urlParams.get('autodeposit') === 'enabled';
  return (
    <>
      <PrintOnlyStyles />
      {/* Progress: complete */}
      <ProgressBar>
        <ProgressFill />
      </ProgressBar>

      <Container>
        <PrintableDiv id="printable" ref={printableRef}>
          {/* Success header */}
          <Center style={{ marginBottom: '2rem' }}>
            <SuccessIconRing>
              <CheckGreenIcon />
            </SuccessIconRing>

            <Title><HtmlRenderer html={intl.messages['interac.success.title']} /></Title>
            <Subtitle><HtmlRenderer html={intl.messages[isAutoDeposit ? 'interac.success.subtitle.autodeposit' : 'interac.success.subtitle']} /></Subtitle>
          </Center>

          {/* Details card */}
          <DCard>
            <DCardBody>
              <CardHeaderRow>
                <img src={InteracImg} alt="Interac" />
                <h2>{intl.messages['interac.success.details']}</h2>
              </CardHeaderRow>

              <div>
                <Row>
                  <LabelText className="strong">
                    {intl.messages['interac.success.amountTransferred']}
                  </LabelText>
                  <ValueText className="big">{amountTranferred}</ValueText>
                </Row>

                <Row>
                  <LabelText>
                    {intl.messages['interac.success.transferFee']}
                  </LabelText>
                  <ValueText>{transferFee}</ValueText>
                </Row>

                <Row>
                  <LabelText>
                    {intl.messages['interac.success.originalAmount']}
                  </LabelText>
                  <ValueText>{originalAmount}</ValueText>
                </Row>

                <Row>
                  <LabelText>
                    {intl.messages['interac.success.transactionId']}
                  </LabelText>
                  <ValueText className="mono">{transactionId}</ValueText>
                </Row>

                <Row>
                  <LabelText>
                    {intl.messages['interac.success.reference']}
                  </LabelText>
                  <ValueText className="mono">{processorReference}</ValueText>
                </Row>

                {recipientEmail && (
                  <Row>
                    <LabelText>
                      {intl.messages['interac.success.recipientEmail']}
                    </LabelText>
                    <Breakable>{recipientEmail}</Breakable>
                  </Row>
                )}

                <Row>
                  <LabelText>
                    {intl.messages['interac.success.transferMethod']}
                  </LabelText>
                  <ValueText><HtmlRenderer html={intl.messages['transfer.interac.title']} /></ValueText>
                </Row>

                <Row style={{ borderBottom: 'none' }}>
                  <LabelText>
                    {intl.messages['interac.success.dateTime']}
                  </LabelText>
                  <ValueText>{transferDate}</ValueText>
                </Row>
              </div>
              {/* Trademark Disclaimer */}
              <div style={{ margin: '1rem 0', fontSize: '0.875rem', color: '#555' }}>
                <p><HtmlRenderer html={intl.messages['transfer.trademark.disclaimer.interac']} /></p>
              </div>
            </DCardBody>
          </DCard>
        </PrintableDiv>

        {/* Actions */}
        <ButtonRow>
          <OutlineButton type="button" onClick={handleDownload}>
            <DownloadIcon />
            {intl.messages['interac.success.download']}
          </OutlineButton>

          <OutlineButton type="button" onClick={handleShare}>
            <ShareIcon />
            {intl.messages['interac.success.share']}
          </OutlineButton>
        </ButtonRow>
      </Container>
    </>
  );
}

export default InteracSuccess;
